/**
 * LoginPromptSheet - 비로그인 사용자 로그인 유도 바텀시트
 *
 * 체크아웃 진입 시 세션 1회만 표시.
 * 배경 탭 또는 "비회원으로 계속" 클릭 시 닫힘.
 * 닫힌 후 sessionStorage에 플래그 저장 → 재표시 방지.
 */
import { useEffect } from 'react';
import { CreditCard, Gift, ClipboardList, X } from 'lucide-react';

interface LoginPromptSheetProps {
  isOpen: boolean;
  onLogin: () => void;
  onContinueAsGuest: () => void;
  t: {
    title: string;
    benefit1: string;
    benefit2: string;
    benefit3: string;
    login: string;
    continueAsGuest: string;
    /**
     * 닫기 버튼 (X) aria-label — 로케일별 정합 강제 (#410).
     * 시각 라벨이 없는 icon-only 버튼이므로 별도 i18n 키 분리.
     */
    closeAriaLabel: string;
  };
}

export function LoginPromptSheet({
  isOpen,
  onLogin,
  onContinueAsGuest,
  t,
}: LoginPromptSheetProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onContinueAsGuest}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-prompt-title"
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-[20px] px-[24px] pt-[28px] pb-[36px] shadow-2xl animate-in slide-in-from-bottom duration-300"
      >
        <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-[24px]" />
        <button
          onClick={onContinueAsGuest}
          className="absolute top-[20px] right-[20px] p-[6px] text-muted-foreground hover:text-foreground rounded-full hover:bg-muted transition-colors"
          aria-label={t.closeAriaLabel}
        >
          <X className="w-[20px] h-[20px]" />
        </button>
        <h2
          id="login-prompt-title"
          className="text-[20px] font-bold text-foreground text-center mb-[20px]"
        >
          {t.title}
        </h2>
        <ul className="space-y-[14px] mb-[28px]">
          <li className="flex items-center gap-[14px]">
            <div className="w-[40px] h-[40px] rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-[20px] h-[20px] text-primary" />
            </div>
            <span className="text-[15px] text-foreground">{t.benefit1}</span>
          </li>
          <li className="flex items-center gap-[14px]">
            <div className="w-[40px] h-[40px] rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Gift className="w-[20px] h-[20px] text-primary" />
            </div>
            <span className="text-[15px] text-foreground">{t.benefit2}</span>
          </li>
          <li className="flex items-center gap-[14px]">
            <div className="w-[40px] h-[40px] rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="w-[20px] h-[20px] text-primary" />
            </div>
            <span className="text-[15px] text-foreground">{t.benefit3}</span>
          </li>
        </ul>
        <div className="space-y-[10px]">
          <button
            onClick={onLogin}
            className="w-full h-[52px] bg-primary text-white font-bold text-[16px] rounded-[10px] hover:bg-primary-hover transition-colors"
          >
            {t.login}
          </button>
          <button
            onClick={onContinueAsGuest}
            className="w-full h-[44px] text-[14px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {t.continueAsGuest}
          </button>
        </div>
      </div>
    </>
  );
}
